import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { Button, Input } from "@/shared/ui";
import { useAppDispatch, useAppSelector } from "@/shared/hooks";
import { PROFILE_PAGE_ROUTE, LOGIN_PAGE_ROUTE } from "@/shared/utils";
import { AuthLayout } from "../components";
import { useChangePasswordMutation } from "../api";
import { clearMustChangePassword } from "../slice";
import { mapBackendErrorToI18nKey } from "../utils/errorMapping";
import { changePasswordSchema, type ChangePasswordFormValues } from "../validation";

function getErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "data" in error) {
    const fetchError = error as FetchBaseQueryError;

    if (fetchError.data && typeof fetchError.data === "object") {
      const errorData = fetchError.data as { detail?: string; error?: string; message?: string };

      if (errorData.detail && typeof errorData.detail === "string") {
        return mapBackendErrorToI18nKey(errorData.detail);
      }

      if (errorData.error && typeof errorData.error === "string") {
        return mapBackendErrorToI18nKey(errorData.error);
      }

      if (errorData.message && typeof errorData.message === "string") {
        return mapBackendErrorToI18nKey(errorData.message);
      }
    }

    if (fetchError.status === "FETCH_ERROR" || fetchError.status === "PARSING_ERROR") {
      return "errors.server_unavailable";
    }

    if (typeof fetchError.status === "number" && fetchError.status >= 500) {
      return "errors.server_unavailable";
    }
  }

  return "errors.unknown";
}

export default function ChangePasswordPage() {
  const { t } = useTranslation("ChangePasswordPage");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const mustChangePassword = useAppSelector((state) => state.auth.mustChangePassword);

  const tempToken = sessionStorage.getItem("temp_token") ?? undefined;
  const isTempFlow = Boolean(tempToken);

  const [changePassword, { isLoading, error }] = useChangePasswordMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      old_password: "",
      new_password: "",
      confirm_password: "",
    },
  });

  const isAuth = useAppSelector((state) => state.auth.isAuth);

  useEffect(() => {
    if (!isAuth && !mustChangePassword && !isTempFlow) {
      navigate(LOGIN_PAGE_ROUTE, { replace: true });
    }
  }, [isAuth, mustChangePassword, isTempFlow, navigate]);

  async function onSubmit(data: ChangePasswordFormValues) {
    try {
      await changePassword({
        data: {
          old_password: data.old_password || undefined,
          new_password: data.new_password,
        },
        tempToken: tempToken,
      }).unwrap();

      sessionStorage.removeItem("temp_token");
      dispatch(clearMustChangePassword());
      navigate(isTempFlow || mustChangePassword ? LOGIN_PAGE_ROUTE : PROFILE_PAGE_ROUTE, { replace: true });
    } catch (err) {
      void err;
    }
  }

  if (!isAuth && !mustChangePassword && !isTempFlow) {
    return null;
  }

  return (
    <AuthLayout>
      <title>{t("meta.title")}</title>
      <meta name="description" content={t("meta.description")} />
      <div className="w-full">
        <form onSubmit={handleSubmit(onSubmit)} className="max-w-sm mx-auto mt-12 flex flex-col gap-6">
          <h1 className="text-center text-display-xs content-base-primary">{t("title")}</h1>
          {!mustChangePassword && !isTempFlow && (
            <Input
              type="password"
              label={t("oldPassword.label")}
              placeholder={t("oldPassword.placeholder")}
              autoComplete="current-password"
              {...register("old_password")}
              error={errors.old_password?.message ? t(errors.old_password.message) : undefined}
            />
          )}

          <Input
            type="password"
            label={t("newPassword.label")}
            placeholder={t("newPassword.placeholder")}
            autoComplete="new-password"
            {...register("new_password")}
            error={errors.new_password?.message ? t(errors.new_password.message) : undefined}
          />

          <div className="flex flex-col gap-2">
            <Input
              type="password"
              label={t("confirmPassword.label")}
              placeholder={t("confirmPassword.placeholder")}
              autoComplete="new-password"
              {...register("confirm_password")}
              error={errors.confirm_password?.message ? t(errors.confirm_password.message) : undefined}
            />
            <p className="text-body-regular-xs content-base-secondary">{t("passwordHint")}</p>
          </div>

          {error && !isLoading && <p className="content-action-negative">{t(getErrorMessage(error))}</p>}
          <Button size="lg" variant="primary" disabled={isLoading} type="submit">
            {isLoading ? t("loading") : t("button")}
          </Button>
        </form>
      </div>
    </AuthLayout>
  );
}
